import React, { useState, useEffect,useRef } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Eye, EyeOff, Trash2, Plus } from 'lucide-react';

const COLUMN_TYPES = ['TEXT', 'NUMBER', 'DATE', 'CURRENCY', 'FORMULA'];
const LOCKED_COLUMNS = ['item','unit','igst','cgst','scgt']; // A1 - Item, G1 - Quantity
const MUST_COLUMNS = ['item','qty','rate','total']; // A1 - Item, G1 - Quantity

function updateColumnIds(columns) {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  return columns.map((col, i) => {
    const letter = alphabet[i % 26];
    const suffix = Math.floor(i / 26);
    const id = suffix ? `${letter}${suffix + 1}` : `${letter}1`;
    return { ...col, id };
  });
}
function updateFormulaReferences(newColumns, oldColumns) {
  const nameToOldId = {};
  oldColumns.forEach((col) => {
    if (col.column_name) {
      nameToOldId[col.column_name] = col.id;
    }
  });

  const nameToNewId = {};
  newColumns.forEach((col) => {
    if (col.column_name) {
      nameToNewId[col.column_name] = col.id;
    }
  });

  return newColumns.map((col) => {
    const rawFormula = col.formula || col.FORMULA;
    if (col.type === 'FORMULA' && rawFormula) {
      let updatedFormula = rawFormula;

      // Replace each old ID with its new ID based on column_name
      Object.entries(nameToOldId).forEach(([colName, oldId]) => {
        const newId = nameToNewId[colName];
        if (oldId && newId && oldId !== newId) {
          const regex = new RegExp(`\\b${oldId}\\b`, 'g');
          updatedFormula = updatedFormula.replace(regex, newId);
        }
      });

      return {
        ...col,
        formula: updatedFormula,
        FORMULA: undefined, // clean legacy
      };
    }

    return col;
  });
}




// function updateFormulaReferences(columns, oldColumns) {
//   const oldIdMap = {};
//   oldColumns.forEach((col, i) => {
//     oldIdMap[col.id] = columns[i].id; // map oldId -> newId
//   });

//   return columns.map((col) => {
//     if (col.type === 'FORMULA' && col.formula) {
//       let updatedFormula = col.formula;
//       Object.entries(oldIdMap).forEach(([oldId, newId]) => {
//         const regex = new RegExp(`\\b${oldId}\\b`, 'g'); // exact match only
//         updatedFormula = updatedFormula.replace(regex, newId);
//       });
//       return { ...col, formula: updatedFormula };
//     }
//     return col;
//   });
// }

function SortableItem({ id, column, index, updateColumn, toggleVisibility, removeColumn,innerRef }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isLocked = LOCKED_COLUMNS.includes(column.column_name);
  const isMust = MUST_COLUMNS.includes(column.column_name);

  return (
    <div ref={(el) => {
        setNodeRef(el);
        if (innerRef) innerRef.current = el;
      }} style={style} {...attributes} className="grid grid-cols-12 gap-3 items-start py-2 border-b bg-white">
      <div className="col-span-1 flex items-center gap-1 text-sm font-mono">
        <span {...listeners} className="cursor-grab text-gray-400">
          <GripVertical size={14} />
        </span>
        {column.id}
        {/* <small className="text-[10px] text-gray-400">{column.column_name}</small> */}
      </div>
      <div className="col-span-5">
        <input
          value={column.name}
          onChange={(e) => updateColumn(index, 'name', e.target.value)}
          className="w-full border px-2 py-1 rounded text-sm"
          placeholder="Enter column label"
          disabled={isLocked}
        />
      </div>
      <div className="col-span-3">
        <select
          value={column.type}
          onChange={(e) => updateColumn(index, 'type', e.target.value)}
          className="w-full border px-2 py-1 rounded text-sm"
          disabled={isLocked}
        >
          {COLUMN_TYPES.map((type) => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>

        {column.type === 'FORMULA' && !isLocked && (
          <div className="mt-2 flex gap-2 items-center">
            <span className="text-xs px-1 bg-gray-100 border rounded text-gray-500">fx</span>
            <input
              type="text"
              value={column.formula || ''}
              onChange={(e) => {
  updateColumn(index, 'formula', e.target.value);
  updateColumn(index, 'FORMULA', undefined); // clean legacy
}}
              className="w-full border px-2 py-1 rounded text-sm"
              placeholder="=A1*D1"
            />
          </div>
        )}
      </div>
      <div className="col-span-3 flex items-center justify-end gap-3 pr-2 text-gray-600">
        
        {!isMust && (
        <button onClick={() => toggleVisibility(index)} title={column.visible ? 'Hide' : 'Show'}>
          {column.visible ? <Eye size={16} /> : <EyeOff size={16} />}
          <span className="ml-1 text-xs">{column.visible ? 'Hide' : 'Show'}</span>
        </button>)}
        {!isLocked && !isMust && (
          <button onClick={() => removeColumn(index)} title="Remove">
            <Trash2 size={20} />
            <span className="ml-1 text-xs">Remove</span>
          </button>
        )}
      </div>
    </div>
  );
}

export default function CustomColumnManager({ defaultColumns = [], templateColumns = [], onSave, onClose, showGst = true, sameState = true }) {
  const [columns, setColumns] = useState([]);
const scrollTargetRef = useRef(null);
  useEffect(() => {
    const defaultMap = new Map(defaultColumns.map(col => [col.id, col]));
    const result = [...defaultColumns];
    templateColumns.forEach(tc => {
      const idx = result.findIndex(dc => dc.id === tc.id);
      if (idx !== -1) {
        result[idx] = { ...result[idx], ...tc };
      } else {
        result.push(tc);
      }
    });

    // GST logic
    const updated = result.filter(col => {
      if (col.id === 'cgst' || col.id === 'sgst') return showGst && sameState;
      if (col.id === 'igst') return showGst && !sameState;
      if (col.id === 'tax' || col.id === 'gst_group') return showGst ? col.id === 'gst_group' : col.id === 'tax';
      return true;
    });

    setColumns(updated);
  }, [defaultColumns, templateColumns, showGst, sameState]);

  const sensors = useSensors(useSensor(PointerSensor));

const addNewColumn = () => {
  const existingIds = new Set(columns.map(col => col.id));
  const existingNames = new Set(columns.map(col => col.column_name).filter(Boolean));
  let charCode = 65;
  let newId;
  let newColumnName;

  do {
    const letter = String.fromCharCode(charCode);
    newId = `${letter}1`;
    newColumnName = `col_${letter.toLowerCase()}`;
    charCode++;
  } while (existingIds.has(newId) || existingNames.has(newColumnName));

  setColumns([
    ...columns,
    {
      id: newId,                // for display and formula use
      name: '',                 // editable label
      column_name: newColumnName, // stable internal key
      type: 'TEXT',
      visible: true,
      align: 'right',
      width: 'w-32',
    },
  ]);

  setTimeout(() => {
    scrollTargetRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, 100);
};
  const updateColumn = (index, field, value) => {
    const updated = [...columns];
    // no need to give name as we used now unique name.
    // if(field === "name"){
    //   //updated[index]['column_name'] = value;
    // }
    updated[index][field] = value;
    setColumns(updated);
  };

  const removeColumn = (index) => {
    if (LOCKED_COLUMNS.includes(columns[index].id)) return;
    setColumns(columns.filter((_, i) => i !== index));
  };

  const toggleVisibility = (index) => {
    const updated = [...columns];
    updated[index].visible = !updated[index].visible;
    setColumns(updated);
  };
//  function updateFormulaReferencesUsingStableKey(oldColumns, newColumns) {
//   const idToColumnName = {};
//   const columnNameToNewId = {};

//   // Build mapping: oldId → column_name
//   oldColumns.forEach(col => {
//     if (col.id && col.column_name) {
//       idToColumnName[col.id] = col.column_name;
//     }
//   });

//   console.log("Old Cols",idToColumnName);
//   console.log("New Cols",columnNameToNewId);
//   // Build mapping: column_name → newId
//   newColumns.forEach(col => {
//     if (col.column_name && col.id) {
//       columnNameToNewId[col.column_name] = col.id;
//     }
//   });

//   return newColumns.map(col => {
//     if (col.type === 'FORMULA' && col.formula) {
//       let updatedFormula = col.formula;

//       const tokens = col.formula.match(/\b[A-Z]+\d+\b/g); // Match A1, B1, etc.
//       console.log("tokens",updatedFormula);
//       console.log("tokens",tokens);
//       if (tokens) {
//         tokens.forEach(token => {
          
//           const columnName = idToColumnName[token];
//           const newId = columnName ? columnNameToNewId[columnName] : null;

//           if (columnName && newId && token !== newId) {
//             const regex = new RegExp(`\\b${token}\\b`, 'g');
//             updatedFormula = updatedFormula.replace(regex, newId);
//             console.log("updated formula",updatedFormula);
//           }
//         });
//       }

//       // if (updatedFormula !== col.formula) {
//       //   console.log(`✅ Formula updated for ${col.column_name || col.id}`);
//       //   console.log(`BEFORE: ${col.formula}`);
//       //   console.log(`AFTER : ${updatedFormula}`);
//       // }

//       return {
//         ...col,
//         formula: updatedFormula,
//       };
//     }

//     return col;
//   });
// }

function updateFormulaReferencesUsingStableKey(oldColumns, newColumns) {
  const idToColumnName = {};
  const columnNameToNewId = {};

  oldColumns.forEach(col => {
    if (col.id && col.column_name) {
      idToColumnName[col.id] = col.column_name;
    }
  });

  newColumns.forEach(col => {
    if (col.id && col.column_name) {
      columnNameToNewId[col.column_name] = col.id;
    }
  });

  return newColumns.map(col => {
    if (col.type === 'FORMULA' && col.formula) {
      const original = col.formula;
      let updatedFormula = original;

      const tokens = original.match(/\b[A-Z]+\d+\b/g); // A1, B1, etc.
      const replacements = {};

      // First pass: mark replacements
      if (tokens) {
        tokens.forEach(token => {
          const columnName = idToColumnName[token];
          const newId = columnName ? columnNameToNewId[columnName] : null;

          if (columnName && newId && token !== newId) {
            const placeholder = `__REPL_${token}__`;
            replacements[placeholder] = newId;

            // Replace original token with placeholder to prevent double replacement
            const regex = new RegExp(`\\b${token}\\b`, 'g');
            updatedFormula = updatedFormula.replace(regex, placeholder);
          }
        });
      }

      // Second pass: apply new IDs safely
      Object.entries(replacements).forEach(([placeholder, newId]) => {
        updatedFormula = updatedFormula.replaceAll(placeholder, newId);
      });

      if (updatedFormula !== original) {
        console.log(`✅ Formula updated for ${col.column_name || col.id}`);
        console.log(`BEFORE: ${original}`);
        console.log(`AFTER : ${updatedFormula}`);
      }

      return {
        ...col,
        formula: updatedFormula
      };
    }

    return col;
  });
}


const handleDragEnd = ({ active, over }) => {
  if (!over || active.id === over.id) return;

  const oldIndex = columns.findIndex(c => c.id === active.id);
  const newIndex = columns.findIndex(c => c.id === over.id);
  if (oldIndex === -1 || newIndex === -1) return;

  const oldColumns = [...columns];
  const reordered = arrayMove(oldColumns, oldIndex, newIndex);

  // Generate new UI-style ids (A1, B1, etc.)
  const withNewIds = updateColumnIds(reordered);

  // Safely rewrite formulas using stable reference
  console.log('Column Names + IDs:');
columns.forEach(col => {
  console.log(`${col.column_name || '[EMPTY]'}`.padEnd(20), '→', col.id);
});

  const updated = updateFormulaReferencesUsingStableKey(oldColumns, withNewIds);

  setColumns(updated);
};

// const handleDragEnd = ({ active, over }) => {
//   if (!over || active.id === over.id) return;

//   const oldIndex = columns.findIndex(c => c.id === active.id);
//   const newIndex = columns.findIndex(c => c.id === over.id);
//   if (oldIndex === -1 || newIndex === -1) return;

//   const oldColumns = [...columns];                       // STEP 1
//   const reordered = arrayMove(oldColumns, oldIndex, newIndex);  // STEP 2
//   const withNewIds = updateColumnIds(reordered);                // STEP 3
//   const updated = updateFormulaReferences(withNewIds, oldColumns); // STEP 4
//   console.log(updated);
//   setColumns(updated);                                   // STEP 5
// };

  const handleSave = () => {
  const visibleIds = columns.filter(c => c.visible).map(c => c.id);
  if (onSave) onSave(visibleIds, columns);  // 🔥 Pass both
  if (onClose) onClose();
};

  // const handleSave = () => {
  //   const jsonData = JSON.stringify(columns);
  //   if (onSave) onSave(jsonData);
  //   console.log('Saved Columns JSON:', jsonData);
  //   if (onClose) onClose();
  // };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center">
  <div className="bg-white w-full sm:w-[700px] max-w-full max-h-[90vh] rounded shadow-lg flex flex-col animate-fadeIn">
    
    {/* Header */}
    <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white z-10">
      <h2 className="text-lg font-semibold">Create Columns</h2>
      <button
        onClick={addNewColumn}
        className="flex items-center gap-1 text-purple-600 text-sm border border-purple-600 px-3 py-1 rounded hover:bg-purple-50"
      >
        <Plus size={14} /> Add New Column
      </button>
    </div>

    {/* Sticky column labels */}
    <div className="grid grid-cols-12 gap-3 font-medium text-gray-700 border-b pb-2 px-4 sticky top-[64px] bg-white z-10">
      <div className="col-span-1">ID</div>
      <div className="col-span-5">Column Name</div>
      <div className="col-span-3">Column Type</div>
      <div className="col-span-3 text-right">Actions</div>
    </div>

    {/* Scrollable content */}
    <div className="overflow-y-auto px-4 flex-1">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={columns.map(col => col.id)} strategy={verticalListSortingStrategy}>
          {columns.map((col, index) => (
            <SortableItem
              key={col.id}
              id={col.id}
              column={col}
              index={index}
              updateColumn={updateColumn}
              toggleVisibility={toggleVisibility}
              removeColumn={removeColumn}
              innerRef={index === columns.length - 1 ? scrollTargetRef : null}
            />
          ))}
        </SortableContext>
      </DndContext>
    </div>

    {/* Sticky Footer */}
    <div className="pt-4 px-4 pb-4 border-t bg-white sticky bottom-0 z-10">
      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="text-sm px-4 py-2 rounded border">Cancel</button>
        <button onClick={handleSave} className="bg-blue-600 text-white px-4 py-2 rounded text-sm">Save Columns</button>
      </div>
    </div>

  </div>
</div>


  );
}
